import { useState, useEffect, useCallback, useRef } from 'react';
import { Plus, X, Sparkles, Loader2, Send } from 'lucide-react';
import { suggestBulk, formatGenerationError } from '../../lib/generationClient';
import { API_BASE } from '../../lib/apiBase';
import { resolveBeratungsthema } from '../../lib/beratungsthema';
import { GeneratingTipsPopup } from '../GeneratingTipsPopup';
import { RichListInput } from '../RichListInput';
import { htmlToPlainText } from '../../lib/richText';

export interface Section3Data {
  durchfuehrungsschritte: Array<{ text: string }>;
}

interface Section3ExecutionProps {
  darkMode: boolean;
  onDataChange: (data: Section3Data) => void;
  onGenerate: () => void;
  customerData?: {
    name: string;
    wirtschaftszweig?: string;
    geschaeftsgegenstand?: string;
    ort?: string;
  };
  formData?: Record<string, string>;
  initialData?: Section3Data;
  analyseText?: string;
  isAdmin?: boolean;
}

export function Section3Execution({ darkMode, onDataChange, onGenerate, customerData, formData, initialData, analyseText, isAdmin }: Section3ExecutionProps) {
  const [executionStepsEntries, setExecutionStepsEntries] = useState<Array<{ text: string }>>(
    initialData?.durchfuehrungsschritte || [{ text: '' }]
  );

  const [showPromptPopup, setShowPromptPopup] = useState(false);
  const [editablePrompt, setEditablePrompt] = useState('');
  const [promptError, setPromptError] = useState<string | null>(null);
  const [bulkSuggesting, setBulkSuggesting] = useState(false);
  const [suggestPromptSource, setSuggestPromptSource] = useState<'standard' | 'lokal' | 'bearbeitet'>('standard');
  const [tipsPopupOpen, setTipsPopupOpen] = useState(false);
  const [tipsPopupError, setTipsPopupError] = useState<string | null>(null);

  const onDataChangeRef = useRef(onDataChange);
  onDataChangeRef.current = onDataChange;

  useEffect(() => {
    onDataChangeRef.current({ durchfuehrungsschritte: executionStepsEntries });
  }, [executionStepsEntries]);

  const STORAGE_KEY = 'suggest-prompt-execution';

  const buildDefaultPrompt = () => {
    const existing = executionStepsEntries.map(e => htmlToPlainText(e.text).trim()).filter(Boolean);
    const name = customerData?.name || '';
    const branche = customerData?.wirtschaftszweig || '';
    const thema = resolveBeratungsthema(formData || {});
    const cleanAnalyse = (analyseText || '').replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
    return `Erstelle Durchfuehrungsschritte fuer Abschnitt 3 "Angaben zur Auftragsdurchfuehrung und Erlaeuterung des Vorgehens".

WICHTIGSTE REGEL:
- Generiere IMMER Vorschlaege, auch wenn Informationen knapp sind
- Keine Rueckfragen, keine Erklaerungen, keine Kommentare
- Antworte ausschliesslich mit dem JSON-Objekt

UNTERNEHMEN:
- Name: ${name}
- Branche/WZ: ${branche}
- Beratungsthema: ${thema}

${cleanAnalyse ? `ANALYSE AUS ABSCHNITT 2 (darauf aufbauen!):\n${cleanAnalyse}\n` : 'Kein Abschnitt-2-Text vorhanden – leite typische Analyseschritte der Branche ab.\n'}
${existing.length ? `Bereits vorhandene Schritte (NICHT wiederholen):\n${existing.map(s => `- ${s}`).join('\n')}\n` : ''}

AUFGABE:
Beschreibe die methodische Vorgehensweise der Beratung: WAS wurde untersucht, WELCHE Methodik wurde angewendet, WIE war der Ablauf strukturiert.

ABGRENZUNG (ZWINGEND):
Abschnitt 3 beschreibt NUR den Weg, NICHT das Ziel.
- VERBOTEN: Massnahmen, Handlungsempfehlungen, Massnahmenplan, Loesungen, Optimierung
- VERBOTEN: Bewertungen, Priorisierungen, Ergebnisdarstellungen
- VERBOTEN: "Ergebnis", "Feststellung", "Empfehlung", "priorisiert", "bewertet", "Sicherheitsluecken"
- VERBOTEN: Inhalte die in Abschnitt 4 (Schwachstellen) oder 5 (Massnahmen) gehoeren
- ERLAUBT: "Analyse", "Untersuchung", "Erfassung", "Strukturierung", "Zusammenfuehrung"
- Statt "Massnahmenplan entwickelt" -> "Analyseergebnisse strukturiert aufbereitet"
- Statt "Handlungsempfehlungen praesentiert" -> "Ergebnisse der Analyse strukturiert dargestellt"

CHRONOLOGIE:
- Beginn: Einstieg, Abstimmung, Auftragsklärung
- Mitte: Analyse, Untersuchung, Strukturierung
- Ende: Zusammenfuehrung der Erkenntnisse (OHNE Massnahmen oder Empfehlungen)
- Die letzten 1-2 Schritte beschreiben die strukturierte Zusammenfuehrung, nicht die Ableitung von Massnahmen

FORMULIERUNG:
- Jeder Schritt als kurzer, praegnanter Satz (max. 20 Woerter)
- Chronologisch von Beratungsbeginn bis Abschluss
- Spezifisch fuer Branche und Beratungsthema, keine generischen Floskeln
- Keine rein allgemeinen Aussagen wie "Analyse durchgefuehrt" - jeder Schritt muss einen konkreten methodischen Inhalt zeigen
- Variiere Verben fuer Analyseprozesse (z. B. erfassen, untersuchen, auswerten, strukturieren, sichten, aufnehmen, pruefen)
- Keine zu spezifischen Standards oder Normen (z. B. PCI-DSS), sofern nicht explizit in den Eingaben enthalten
- Der letzte Schritt soll die Zusammenfuehrung als "nachvollziehbare Grundlage fuer die weitere Analyse" oder "strukturierte Grundlage fuer die nachfolgenden Auswertungsschritte" beschreiben
- BAFA-konform: strategische Beratung, keine operative Umsetzung

ANTWORTFORMAT:
{"durchfuehrungsschritte":["..."]}`;
  };

  const applyBulkResponse = (res: any) => {
    const steps = res.durchfuehrungsschritte || res.vorgehensschritte || res.beratungsziele || [];
    if (!steps.length) return false;
    const newSteps = steps.map((t: string) => ({ text: t }));
    setExecutionStepsEntries(newSteps);
    const newData = { durchfuehrungsschritte: newSteps };
    onDataChange(newData);
    try { localStorage.setItem('section3-inputs-backup', JSON.stringify(newData)); } catch {}
    return true;
  };

  const openSuggestPopup = async () => {
    // 1. localStorage
    let saved = localStorage.getItem(STORAGE_KEY);
    // 2. DB Fallback
    if (!saved) {
      try {
        const token = localStorage.getItem('auth_token');
        const res = await fetch(`${API_BASE}/api/prompts?sectionKey=suggest-execution&cardId=bulk`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const data = await res.json();
          if (data?.promptText) {
            saved = data.promptText;
            localStorage.setItem(STORAGE_KEY, saved);
          }
        }
      } catch {}
    }
    setSuggestPromptSource('standard');
    setEditablePrompt(buildDefaultPrompt());
    setPromptError(null);
    setShowPromptPopup(true);
  };

  const handleDirectBulkSuggest = async () => {
    setTipsPopupOpen(true);
    setTipsPopupError(null);
    const prompt = buildDefaultPrompt();
    const result = await suggestBulk<any>({
      sectionId: 'execution',
      customPrompt: prompt,
      context: {
        unternehmen: customerData?.name || '',
        branche: customerData?.wirtschaftszweig || '',
        berichtstitel: formData?.['berichtstitel'] || '',
      },
    });
    if (!result.ok) {
      setTipsPopupError(formatGenerationError(result.error));
      const autoCloseMs = result.error.code === 'RATE_LIMITED' || result.error.code === 'ALREADY_RUNNING' ? 8000 : 6000;
      setTimeout(() => { setTipsPopupOpen(false); setTipsPopupError(null); }, autoCloseMs);
      return;
    }
    applyBulkResponse(result.data);
    setTipsPopupOpen(false);
  };

  const handleSuggestSubmit = async () => {
    setBulkSuggesting(true);
    setPromptError(null);
    localStorage.setItem(STORAGE_KEY, editablePrompt);
    try {
      const token = localStorage.getItem('auth_token');
      // DB-Backup (fire-and-forget)
      fetch(`${API_BASE}/api/prompts/save`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ sectionKey: 'suggest-execution', cardId: 'bulk', promptText: editablePrompt }),
      }).catch(() => {});

      const result = await suggestBulk<any>({
        sectionId: 'execution',
        customPrompt: editablePrompt,
        context: {
          unternehmen: customerData?.name || '',
          branche: customerData?.wirtschaftszweig || '',
          berichtstitel: formData?.['berichtstitel'] || '',
        },
      });
      if (!result.ok) {
        throw new Error(formatGenerationError(result.error));
      }
      applyBulkResponse(result.data);
      setShowPromptPopup(false);
    } catch (err: any) {
      setPromptError(err.message || 'Vorschläge konnten nicht generiert werden');
    } finally {
      setBulkSuggesting(false);
    }
  };

  const removeBtn = (disabled: boolean) =>
    disabled
      ? darkMode
        ? 'p-2.5 rounded-lg border border-slate-600 text-slate-500 cursor-not-allowed transition-colors'
        : 'p-2.5 rounded-lg border border-slate-200 text-slate-300 cursor-not-allowed transition-colors'
      : darkMode
        ? 'p-2.5 rounded-lg border border-slate-600 text-slate-400 hover:bg-red-900/30 hover:border-red-500 hover:text-red-400 transition-colors'
        : 'p-2.5 rounded-lg border border-slate-300 text-slate-600 hover:bg-red-50 hover:border-red-300 hover:text-red-600 transition-colors';

  const addBtnClass = darkMode
    ? 'border-slate-600 text-slate-400 hover:border-blue-500 hover:text-blue-400 hover:bg-blue-900/20'
    : 'border-slate-300 text-slate-600 hover:border-blue-500 hover:text-blue-600 hover:bg-blue-50';

  const inputClass = darkMode
    ? 'bg-slate-600 border-slate-500 text-white placeholder-slate-400'
    : 'border-slate-300';

  return (
    <>
    <div className={`rounded-lg shadow-sm flex-1 ${darkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'} border`}>
      <div className="mx-6 my-6">
        {/* Vorschläge generieren Button */}
        <button
          onClick={isAdmin ? openSuggestPopup : handleDirectBulkSuggest}
          disabled={!customerData}
          className={`w-full flex items-center justify-center gap-2 px-4 py-3 mb-5 rounded-lg font-medium text-sm transition-colors border-2 border-dashed ${
            !customerData
              ? 'opacity-50 cursor-not-allowed'
              : darkMode
                ? 'border-blue-500/50 text-blue-400 hover:bg-blue-900/20 hover:border-blue-400'
                : 'border-blue-400 text-blue-600 hover:bg-blue-50 hover:border-blue-500'
          }`}
        >
          <Sparkles className="w-4 h-4" />
          Vorschläge generieren
        </button>

        <div className="space-y-5">
          <div>
            <label className={`block text-sm font-medium mb-3 ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}>
              Vorgehensweise und Durchführungsschritte
            </label>
            <div className="space-y-3">
              {executionStepsEntries.map((entry, index) => (
                <div key={index} className="flex items-start gap-3">
                  <RichListInput
                    value={entry.text}
                    onChange={(html) => {
                      const updated = [...executionStepsEntries];
                      updated[index] = { text: html };
                      setExecutionStepsEntries(updated);
                    }}
                    placeholder="Beschreiben Sie einen Durchführungsschritt..."
                    darkMode={darkMode}
                  />
                  <button
                    onClick={() => {
                      if (executionStepsEntries.length > 1)
                        setExecutionStepsEntries(executionStepsEntries.filter((_, i) => i !== index));
                    }}
                    disabled={executionStepsEntries.length === 1}
                    className={removeBtn(executionStepsEntries.length === 1)}
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              ))}
              <button
                onClick={() => setExecutionStepsEntries([...executionStepsEntries, { text: '' }])}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg border-2 border-dashed transition-colors text-sm font-medium ${addBtnClass}`}
              >
                <Plus className="w-4 h-4" />
                Weiteren Schritt hinzufügen
              </button>
            </div>
          </div>
        </div>

        <div className={`flex justify-end pt-6 mt-6 border-t ${darkMode ? 'border-slate-600' : 'border-slate-200'}`}>
          <button
            onClick={onGenerate}
            className={`w-full px-4 py-2 rounded-lg border transition-colors text-sm font-medium shadow-sm ${
              darkMode ? 'bg-slate-700 text-slate-200 border-slate-600 hover:bg-slate-600' : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50'
            }`}
          >
            ✨ Text generieren
          </button>
        </div>
      </div>
    </div>

    {/* Prompt Popup */}
    {showPromptPopup && (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={!bulkSuggesting ? () => setShowPromptPopup(false) : undefined} />
        <div className={`relative w-full max-w-2xl max-h-[85vh] flex flex-col rounded-xl shadow-2xl ${darkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'} border`}>
          <div className={`flex items-center justify-between p-4 sm:p-6 border-b ${darkMode ? 'border-slate-700' : 'border-slate-200'}`}>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg flex items-center justify-center">
                <Sparkles className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-slate-900'}`}>Vorschläge generieren</h2>
                <div className={`flex items-center gap-2 text-xs mt-0.5 ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                  <span className={`px-1.5 py-0.5 rounded font-medium ${
                    suggestPromptSource === 'standard'
                      ? darkMode ? 'bg-green-900/30 text-green-400' : 'bg-green-100 text-green-700'
                      : suggestPromptSource === 'lokal'
                        ? darkMode ? 'bg-yellow-900/30 text-yellow-400' : 'bg-yellow-100 text-yellow-700'
                        : darkMode ? 'bg-blue-900/30 text-blue-400' : 'bg-blue-100 text-blue-700'
                  }`}>
                    {suggestPromptSource === 'standard' ? 'Standard' : suggestPromptSource === 'lokal' ? 'Lokal gespeichert' : 'Bearbeitet'}
                  </span>
                  <span>· {editablePrompt.length} Z. · {new Date().toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: '2-digit' })} {new Date().toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}</span>
                </div>
              </div>
            </div>
            <button onClick={() => setShowPromptPopup(false)} disabled={bulkSuggesting} className={`p-2 rounded-lg transition-colors ${darkMode ? 'hover:bg-slate-700 text-slate-400' : 'hover:bg-slate-100 text-slate-500'} disabled:opacity-50`}>
              <X className="w-5 h-5" />
            </button>
          </div>
          <div className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-4">
            <div>
              <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}>Prompt (bearbeitbar)</label>
              <textarea translate="no" value={editablePrompt} onChange={(e) => { setEditablePrompt(e.target.value); setSuggestPromptSource('bearbeitet'); }} disabled={bulkSuggesting} rows={16} className={`w-full px-4 py-3 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-y font-mono leading-relaxed ${darkMode ? 'bg-slate-700 border-slate-600 text-white placeholder-slate-400' : 'bg-slate-50 border-slate-300 text-slate-900'} disabled:opacity-60`} />
            </div>
            {promptError && (
              <div className="p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
                <p className="text-sm text-red-700 dark:text-red-300">{promptError}</p>
              </div>
            )}
            {bulkSuggesting && (
              <div className={`p-3 rounded-lg flex items-center gap-3 ${darkMode ? 'bg-purple-900/20 border-purple-800' : 'bg-purple-50 border-purple-200'} border`}>
                <Loader2 className={`w-4 h-4 animate-spin ${darkMode ? 'text-purple-400' : 'text-purple-600'}`} />
                <p className={`text-sm ${darkMode ? 'text-purple-300' : 'text-purple-700'}`}>Vorschläge werden generiert...</p>
              </div>
            )}
          </div>
          <div className={`flex items-center justify-between gap-3 p-4 sm:p-6 border-t ${darkMode ? 'border-slate-700' : 'border-slate-200'}`}>
            <button
              onClick={() => {
                localStorage.removeItem(STORAGE_KEY);
                setEditablePrompt(buildDefaultPrompt());
                setSuggestPromptSource('standard');
                setPromptError(null);
              }}
              disabled={bulkSuggesting}
              className={`text-sm ${darkMode ? 'text-slate-400 hover:text-slate-200' : 'text-slate-500 hover:text-slate-700'} transition-colors`}
            >
              ↺ Prompt zurücksetzen
            </button>
            <button onClick={handleSuggestSubmit} disabled={bulkSuggesting || !editablePrompt.trim()} className="flex items-center gap-2 px-6 py-2.5 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors text-sm font-medium shadow-sm disabled:opacity-50 disabled:cursor-not-allowed">
              <span className="inline-flex">
                {bulkSuggesting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              </span>
              <span>{bulkSuggesting ? 'Wird generiert...' : 'Abschicken'}</span>
            </button>
          </div>
        </div>
      </div>
    )}

    <GeneratingTipsPopup isOpen={tipsPopupOpen} darkMode={darkMode} error={tipsPopupError} />
    </>
  );
}
